import axios from 'axios';
import { COMMISSION_ENDPOINTS } from '../constants/api';

/**
 * Match Results Persistence Service
 * Handles saving and loading match results for persistent storage
 */
class MatchResultsService {
  /**
   * Save match results to the backend
   * @param {Object} matchResults - The match results from BackendMatchingService
   * @param {string} hubspotDataHash - Hash of HubSpot data used
   * @param {string} wellskyDataHash - Hash of WellSky data used
   * @returns {Promise} - API response
   */
  async saveMatchResults(matchResults, hubspotDataHash, wellskyDataHash) {
    try {
      console.log('💾 Saving match results to backend...');
      console.log(`📊 Saving ${matchResults.matches?.length || 0} matches and ${matchResults.unmatched?.length || 0} unmatched records`);

      const response = await axios.post(COMMISSION_ENDPOINTS.MATCH_RESULTS, {
        matches: matchResults.matches || [],
        unmatched: matchResults.unmatched || [],
        summary: matchResults.summary || {},
        hubspotDataHash,
        wellskyDataHash
      });

      console.log('✅ Match results saved successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to save match results:', error);
      throw error;
    }
  }

  /**
   * Load the latest match results from the backend
   * @returns {Promise} - API response with match results
   */
  async getLatestMatchResults() {
    try {
      console.log('📂 Loading latest match results from backend...');

      const response = await axios.get(COMMISSION_ENDPOINTS.MATCH_RESULTS_LATEST);

      console.log('✅ Latest match results loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to load match results:', error);
      throw error;
    }
  }

  /**
   * Check if a refresh is needed based on data changes
   * @param {string} hubspotDataHash - Current HubSpot data hash
   * @param {string} wellskyDataHash - Current WellSky data hash
   * @returns {Promise} - API response indicating if refresh is needed
   */
  async checkRefreshNeeded(hubspotDataHash, wellskyDataHash) {
    try {
      console.log('🔍 Checking if refresh is needed...');

      const response = await axios.post(COMMISSION_ENDPOINTS.MATCH_RESULTS_CHECK_REFRESH, {
        hubspotDataHash,
        wellskyDataHash
      });

      console.log('✅ Refresh check completed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to check refresh status:', error);
      throw error;
    }
  }

  /**
   * Get matching sessions history
   * @param {number} limit - Number of sessions to retrieve
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - API response with sessions
   */
  async getMatchingSessions(limit = 10, offset = 0) {
    try {
      console.log(`📂 Loading matching sessions (limit: ${limit}, offset: ${offset})...`);

      const response = await axios.get(COMMISSION_ENDPOINTS.MATCHING_SESSIONS, {
        params: { limit, offset }
      });

      console.log('✅ Matching sessions loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to load matching sessions:', error);
      throw error;
    }
  }

  /**
   * Get match results for a specific session
   * @param {string} sessionId - The session ID
   * @param {number} limit - Number of results to retrieve
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - API response with session results
   */
  async getSessionMatchResults(sessionId, limit = 100, offset = 0) {
    try {
      console.log(`📂 Loading match results for session ${sessionId}...`);

      const response = await axios.get(`${COMMISSION_ENDPOINTS.MATCHING_SESSIONS}/${sessionId}/results`, {
        params: { limit, offset }
      });

      console.log('✅ Session match results loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to load session match results:', error);
      throw error;
    }
  }

  /**
   * Generate a hash for data to detect changes
   * @param {Array|Object} data - Data to hash
   * @returns {string} - Simple hash of the data
   */
  generateDataHash(data) {
    if (!data) return null;
    
    // Simple hash based on data length and first/last items
    const dataArray = Array.isArray(data) ? data : Object.values(data);
    if (dataArray.length === 0) return 'empty';
    
    // Create a more stable hash based on data structure
    const length = dataArray.length;
    
    // Get a few sample items for hashing (first, middle, last)
    const firstItem = dataArray[0] || {};
    const middleItem = dataArray[Math.floor(length / 2)] || {};
    const lastItem = dataArray[length - 1] || {};
    
    // Use specific fields that are stable across data loads
    const getStableFields = (item) => {
      // For HubSpot deals
      if (item.id || item.recordId) {
        return `${item.id || item.recordId}-${item.clientName || item.dealName || ''}`;
      }
      // For WellSky records
      if (item.databaseId) {
        return `${item.databaseId}-${item.fullName || item.clientName || ''}`;
      }
      // Fallback
      return JSON.stringify(item);
    };
    
    const firstStable = getStableFields(firstItem);
    const middleStable = getStableFields(middleItem);
    const lastStable = getStableFields(lastItem);
    
    // Create a deterministic hash string
    const hashString = `${length}-${firstStable}-${middleStable}-${lastStable}`;
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < hashString.length; i++) {
      const char = hashString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return Math.abs(hash).toString(16);
  }

  /**
   * Process saved match results into the format expected by the UI
   * @param {Object} savedResults - Results from getLatestMatchResults
   * @returns {Object} - Processed results for UI
   */
  processSavedMatchResults(savedResults) {
    if (!savedResults.data || !savedResults.data.hasResults) {
      return {
        matches: [],
        unmatched: [],
        summary: {
          totalHubspotRecords: 0,
          totalWellskyRecords: 0,
          totalMatches: 0,
          totalUnmatched: 0,
          matchRate: '0%',
          processingTime: 0
        }
      };
    }

    const { matches, unmatched, summary } = savedResults.data;

    return {
      matches: matches || [],
      unmatched: unmatched || [],
      summary: {
        totalHubspotRecords: summary.totalHubspotRecords || 0,
        totalWellskyRecords: summary.totalWellskyRecords || 0,
        totalMatches: summary.totalMatches || 0,
        totalUnmatched: summary.totalUnmatched || 0,
        matchRate: summary.matchRate || '0%',
        processingTime: summary.processingTime || 0,
        lastMatchedAt: summary.lastMatchedAt
      },
      sessionInfo: savedResults.data.session
    };
  }
}

export default new MatchResultsService();
